// AudioPlayer.jsx - Native HTML5 audio player
import React, { useState, useRef, useEffect } from 'react'

export default function AudioPlayer({ 
  audioUrl, 
  fileName = 'Audio file',
  className = '' 
}) {
  const [isPlaying, setIsPlaying] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [duration, setDuration] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)
  const [error, setError] = useState(null)
  
  const audioRef = useRef(null)
  const progressIntervalRef = useRef(null)

  // Format time helper
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00'
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  // Toggle play/pause
  const togglePlay = async () => {
    const audio = audioRef.current
    if (!audio) return

    try {
      if (isPlaying) {
        audio.pause()
        setIsPlaying(false)
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current)
          progressIntervalRef.current = null
        }
      } else {
        setIsLoading(true)
        setError(null)
        
        if (audio.ended) {
          audio.currentTime = 0
        }
        
        // 🔥 Set source if not already set
        if (!audio.src || audio.src !== audioUrl) {
          audio.src = audioUrl
          audio.load()
        }
        
        const playPromise = audio.play()
        
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              setIsLoading(false)
              setIsPlaying(true)
              
              if (progressIntervalRef.current) {
                clearInterval(progressIntervalRef.current)
              }
              progressIntervalRef.current = setInterval(() => {
                if (audioRef.current) {
                  const current = audioRef.current.currentTime
                  const total = audioRef.current.duration || 1
                  setProgress((current / total) * 100)
                  setCurrentTime(current)
                }
              }, 100)
            })
            .catch((err) => {
              console.error('Playback error:', err)
              setIsLoading(false)
              setError('Unable to play audio')
              setIsPlaying(false)
            })
        }
      }
    } catch (err) {
      console.error('Toggle play error:', err)
      setIsLoading(false)
      setError('Unable to play audio')
    }
  }

  // Handle audio ended
  const handleEnded = () => {
    setIsPlaying(false)
    setProgress(0)
    setCurrentTime(0)
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current)
      progressIntervalRef.current = null
    }
  }

  // Handle audio error
  const handleError = (e) => {
    console.error('Audio error:', e)
    setIsLoading(false)
    setIsPlaying(false)
    setError('Failed to load audio')
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current)
      progressIntervalRef.current = null
    }
  }

  // Handle loaded metadata
  const handleLoadedMetadata = () => {
    const audio = audioRef.current
    if (audio) {
      setDuration(audio.duration)
      setIsLoading(false)
    }
  }

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current)
        progressIntervalRef.current = null
      }
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current.src = ''
      }
    }
  }, [])

  return (
    <div className={`audio-player ${className}`}>
      <audio
        ref={audioRef}
        preload="metadata"
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        onError={handleError}
        src={audioUrl}
      />
      
      <div className="audio-player-content">
        <button 
          className={`audio-play-btn ${isPlaying ? 'playing' : ''}`}
          onClick={togglePlay}
          disabled={isLoading}
          aria-label={isPlaying ? 'Pause' : 'Play'}
        >
          {isLoading ? (
            <span className="audio-spinner">⟳</span>
          ) : isPlaying ? (
            '⏸'
          ) : (
            '▶'
          )}
        </button>
        
        <div className="audio-info">
          <div className="audio-file-name">{fileName}</div>
          <div className="audio-progress-container">
            <div className="audio-progress-bar">
              <div 
                className={`audio-progress-fill ${isPlaying ? 'playing' : ''}`}
                style={{ width: `${Math.min(progress, 100)}%` }}
              />
            </div>
            <div className="audio-time">
              {isPlaying ? formatTime(currentTime) : '0:00'} / {formatTime(duration)}
            </div>
          </div>
        </div>
        
        {error && (
          <div className="audio-error">
            ⚠️ {error}
          </div>
        )}
      </div>
    </div>
  )
}